import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'

import * as totemReportsActions from 'action/totemReports'

import ContentHeader from 'component/utils/ContentHeader'
import Breadcrumb from 'component/Breadcrumb'
import Row from 'component/utils/Row'
import Col from 'component/utils/Col'
import ReportsFilter from 'component/totemReports/ReportsFilter'
import TotemHeaderCards from 'component/totemReports/TotemHeaderCards'
import TotemCharts from 'component/totemReports/TotemCharts'
import ModalReportDetails from 'component/totemReports/ModalReportDetails'

import { ToastContainer } from 'react-toastify'
import { renderToastWarn } from 'utils/ToastUtils'

import { FORMAT_DATE_TRUNC } from 'constants/config'

const WAITING_TIME_THRESHOLD = 10
const ABOVE_OPTION = 'timeAbove'
const BELLOW_OPTION = 'timeUnder'
const ALL_OPTION = 'timeAll'

class TotemReportsContainer extends PureComponent {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    error: PropTypes.object,
    reports: PropTypes.object,
    translate: PropTypes.func.isRequired,
    fetchTotemReports: PropTypes.func.isRequired,
  }

  constructor() {
    super()

    this.state = {
      centerFilter: true,
      sideFilter: false,
      timeFilter: '',
      preLoadedValues: {},
      showModalDetails: false,
      modalOption: ALL_OPTION,
      modalData: [],
      modalOrderDetail: false,
      salesOrderDetailInformation: {},
    }
  }

  componentDidUpdate(prevProps) {
    const { error } = this.props

    if (error && error !== prevProps.error) {
      renderToastWarn(error.message.dsMessage)
    }
  }

  renderFilterAndTable = () => {
    const { translate } = this.props
    const { sideFilter } = this.state

    return (
      <div className='totemreports-filter-table-body'>
        <Row>
          <Col xs={12}>
            <button
              className='totemreports-filter-button totemreports-title'
              onClick={this.handleSideFilterMenuCollapseChange}
            >
              <i
                className={`glyph glyph-grey glyph-${
                  sideFilter ? 'double-arrow-left' : 'double-arrow-right'
                } icon-size`}
              />
              {translate('totem.reports.title.filter')}
            </button>
          </Col>
        </Row>
        {sideFilter ? (
          <Row>
            <Col xs={3}>{this.renderReportsFilter()}</Col>
            <Col xs={9}>{this.renderTable()}</Col>
          </Row>
        ) : (
          <Row>{this.renderTable()}</Row>
        )}
      </div>
    )
  }

  closeModalDetails = () => {
    this.setState({ showModalDetails: false })
  }

  openModalDetails = (modalOption) => {
    const { reports } = this.props
    const { timeFilter } = this.state
    let processedData

    if (
      reports.queueReportItemPresenterList &&
      reports.queueReportItemPresenterList.length > 0
    ) {
      switch (modalOption) {
        case ABOVE_OPTION:
          processedData = reports.queueReportItemPresenterList.filter(
            (item) => item.waitingTime >= WAITING_TIME_THRESHOLD
          )
          break
        case BELLOW_OPTION:
          processedData = reports.queueReportItemPresenterList.filter(
            (item) => item.waitingTime < WAITING_TIME_THRESHOLD
          )
          break
        case ALL_OPTION:
          processedData = reports.queueReportItemPresenterList
          break
        default:
          processedData = reports.queueReportItemPresenterList.filter(
            (item) => item.createTs === modalOption
          )
          if (timeFilter === ABOVE_OPTION) {
            processedData = processedData.filter(
              (item) => parseInt(item.waitingTime, 10) >= WAITING_TIME_THRESHOLD
            )
          }
          if (timeFilter === BELLOW_OPTION) {
            processedData = processedData.filter(
              (item) => parseInt(item.waitingTime, 10) < WAITING_TIME_THRESHOLD
            )
          }
          break
      }
    }

    this.setState({
      showModalDetails: true,
      modalOption,
      modalData: processedData,
      modalOrderDetail: false,
      salesOrder: {},
    })
  }

  showOrderDetails = (salesOrder) => {
    this.setState({
      salesOrderDetailInformation: salesOrder,
      modalOrderDetail: true,
    })
  }

  hideOrderDetails = () => {
    this.setState({ modalOrderDetail: false, salesOrder: {} })
  }

  handleSideFilterMenuCollapseChange = () => {
    const { sideFilter } = this.state
    const inverseSideFilter = !sideFilter

    this.setState({ sideFilter: inverseSideFilter })
  }

  groupArrayByKey = (array, key) => {
    // eslint-disable-next-line no-extend-native
    Array.prototype.groupBy = function (key) {
      return this.reduce((r, a, i) => {
        if (!i || r[r.length - 1][0][key] !== a[key]) {
          return r.concat([[a]])
        }
        r[r.length - 1].push(a)
        return r
      }, [])
    }

    return array.groupBy(key)
  }

  renderTable = () => {
    const { translate, reports } = this.props
    const { timeFilter } = this.state

    if (
      reports.queueReportItemPresenterList &&
      reports.queueReportItemPresenterList.length > 0
    ) {
      let adaptedReports = reports.queueReportItemPresenterList

      if (timeFilter) {
        if (timeFilter === ABOVE_OPTION) {
          adaptedReports = adaptedReports.filter(
            (bar) => bar.waitingTime >= WAITING_TIME_THRESHOLD
          )
        }
        if (timeFilter === BELLOW_OPTION) {
          adaptedReports = adaptedReports.filter(
            (bar) => bar.waitingTime < WAITING_TIME_THRESHOLD
          )
        }
      }

      const groupedData = this.groupArrayByKey(adaptedReports, 'createTs')
      const data = []

      for (const days of groupedData) {
        let totalTimePerDay = 0
        for (const item of days) {
          totalTimePerDay += parseInt(item.waitingTime, 10)
        }

        const averagePerDay = (totalTimePerDay / days.length).toFixed(2)
        const bar = {
          createTs: days[0].createTs,
          averagePerDay,
          qnt: days.length,
        }

        data.push(bar)
      }

      return (
        <div className='totemreports-table-body'>
          <TotemHeaderCards
            translate={translate}
            headerAverage={reports.averageTime}
            headerAbove={reports.itensAbove}
            headerUnder={reports.itensBellow}
            headerQuantity={reports.qnt}
            calculateWait={this.calculateWait}
            openModalDetails={this.openModalDetails}
          />
          {data.length > 0 && (
            <TotemCharts
              translate={translate}
              data={data.reverse()}
              calculateWait={this.calculateWait}
              openModalDetails={this.openModalDetails}
            />
          )}
        </div>
      )
    }
    return (
      <div className='totemreports-table-body'>
        <div className='notification notification-warning'>
          {translate('totem.reports.no.data.warning')}
        </div>
      </div>
    )
  }

  calculateWait = (time, hourTime) => {
    const tempArray = time.split('.')
    if (tempArray.length > 1) {
      const seconds = Math.floor((tempArray[1] * 60) / 100)
        .toString()
        .padStart(2, '0')
      if (hourTime) {
        if (tempArray[0] >= 60) {
          const tempValue = (tempArray[0] / 60).toFixed(2).toString()
          const hourArray = tempValue.split('.')
          const min = Math.floor((hourArray[1] * 60) / 100)
            .toString()
            .padStart(2, '0')

          return `${hourArray[0].padStart(2, '0')}:${min}:${seconds}`
        }
        return `00:${tempArray[0].toString().padStart(2, '0')}:${seconds}`
      }
      return `${tempArray[0].toString().padStart(2, '0')}:${seconds}`
    }
    if (time >= 60) {
      const tempValue = (time / 60).toFixed(2).toString()
      const hourArray = tempValue.split('.')

      const min = Math.floor((hourArray[1] * 60) / 100)
        .toString()
        .padStart(2, '0')

      return `${hourArray[0].padStart(2, '0')}:${min}:00`
    }
    if (hourTime) {
      return `00:${time.toString().padStart(2, '0')}:00`
    }
    return `${time.toString().padStart(2, '0')}:00`
  }

  renderReportsFilter = () => {
    const { translate } = this.props
    const { centerFilter, preLoadedValues } = this.state

    return (
      <div className='totemreports-filter-body'>
        {centerFilter && (
          <Row>
            <Col xs={12}>
              <h3 className='totemreports-filter-title'>
                {translate('totem.reports.title.container')}
              </h3>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <ReportsFilter
              onSubmit={this.onFilterSubmit}
              translate={translate}
              centerFilter={centerFilter}
              preLoadedValues={preLoadedValues}
            />
          </Col>
        </Row>
      </div>
    )
  }

  onFilterSubmit = (filters) => {
    const { fetchTotemReports, auth } = this.props
    const { centerFilter } = this.state

    this.setState({ preLoadedValues: filters })

    const listBranchs = []

    for (const branch of filters.listRegionsAndBranchs) {
      listBranchs.push(branch.value)
    }

    const adaptedFilters = {
      dtStart: filters.dtStart.format(FORMAT_DATE_TRUNC),
      dtEnd: filters.dtEnd.format(FORMAT_DATE_TRUNC),
      listRegionsAndBranchs: listBranchs,
    }

    if (filters) {
      if (filters.timeFilter) {
        this.setState({ timeFilter: filters.timeFilter })
      }

      fetchTotemReports(auth.user.dsToken, adaptedFilters)
    }

    if (centerFilter) {
      this.setState({ centerFilter: false, sideFilter: false })
    }
  }

  render() {
    const { translate } = this.props
    const {
      centerFilter,
      showModalDetails,
      modalOption,
      modalData,
      modalOrderDetail,
      salesOrderDetailInformation,
    } = this.state

    const BREADCRUMB_STEPS = [
      { link: '/', title: translate('mobile.home') },
      { title: translate('totem') },
      { title: translate('totem.reports.title'), strong: true },
    ]

    return (
      <>
        <ContentHeader>{translate('totem.reports.title')}</ContentHeader>
        <Breadcrumb steps={BREADCRUMB_STEPS} />
        {centerFilter
          ? this.renderReportsFilter()
          : this.renderFilterAndTable()}
        <ToastContainer newestOnTop />
        {showModalDetails && (
          <ModalReportDetails
            closeModalDetails={this.closeModalDetails}
            translate={translate}
            modalOption={modalOption}
            modalData={modalData}
            calculateWait={this.calculateWait}
            modalOrderDetail={modalOrderDetail}
            showOrderDetails={this.showOrderDetails}
            hideOrderDetails={this.hideOrderDetails}
            salesOrderDetailInformation={salesOrderDetailInformation}
          />
        )}
      </>
    )
  }
}

/* istanbul ignore next */
const mapStateToProps = ({ totemReportsReducer }) => ({
  reports: totemReportsReducer.list.records,
  error: totemReportsReducer.list.error,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...totemReportsActions }, dispatch)

const TotemReportsContainerTranslated = translate(null, { wait: true })(
  TotemReportsContainer
)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TotemReportsContainerTranslated)

export { TotemReportsContainer }
